import { useState, useEffect, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { jwtService } from '../services/jwtService';
import toast from 'react-hot-toast';

const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const strictAuth = (process.env.REACT_APP_STRICT_AUTH === 'true') && process.env.NODE_ENV !== 'development';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // React Query cache lives outside this provider, so user-scoped data
  // (memories, usage, admin stats) survives logins/logouts unless we wipe
  // it explicitly. Without this, signing in as a second user briefly shows
  // the previous user's cached data before the background refetch lands.
  const queryClient = useQueryClient();

  // Token management functions
  const scheduleTokenRefresh = (token) => {
    const expirationTime = jwtService.getTokenExpirationTime(token);
    if (!expirationTime) return;
    
    const refreshTime = expirationTime.getTime() - Date.now() - (5 * 60 * 1000); // 5 minutes before expiry
    
    if (refreshTime > 0) {
      setTimeout(() => {
        refreshToken().catch(console.error);
      }, refreshTime);
    }
  };

  const refreshToken = async () => {
    try {
      const currentToken = sessionStorage.getItem('mme_token');
      if (!currentToken) {
        throw new Error('No token to refresh');
      }

      const newToken = await jwtService.refreshToken(currentToken);
      sessionStorage.setItem('mme_token', newToken);
      
      // Update user data with new token info
      const userData = jwtService.getUserFromToken(newToken);
      if (userData) {
        setUser(prev => ({ ...prev, ...userData, token: newToken }));
        scheduleTokenRefresh(newToken);
        return newToken;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, log out user
      await logout();
      throw error;
    }
  };

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = sessionStorage.getItem('mme_token');
        
        if (token) {
          // Validate the JWT token
          const validation = await jwtService.validateJWT(token);
          
          if (validation.valid) {
            // Extract user data from token
            const userData = jwtService.getUserFromToken(token);
            
            if (userData) {
              const finalUserData = {
                ...userData,
                token: token
              };
              setUser(finalUserData);
              setIsAuthenticated(true);
              
              // Schedule token refresh
              scheduleTokenRefresh(token);
              
              // In strict mode, also verify backend connectivity
              if (strictAuth) {
                try {
                  await apiService.getTaggingHealth();
                } catch (error) {
                  console.warn('Backend health check failed:', error);
                  toast.error('Backend services unavailable');
                }
              }
            } else {
              throw new Error('Invalid token payload');
            }
          } else {
            throw new Error(validation.error || 'Invalid token');
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        
        // Clear invalid authentication data
        sessionStorage.removeItem('mme_token');
        sessionStorage.removeItem('mme_user_id');
        sessionStorage.removeItem('mme_user_name');
        sessionStorage.removeItem('mme_user_email');
        sessionStorage.removeItem('mme_user_role');
        sessionStorage.removeItem('mme_org_id');
        
        setUser(null);
        setIsAuthenticated(false);
        
        if (strictAuth) {
          toast.error('Session expired. Please login again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      
      // Generate JWT token using the JWT service
      const token = await jwtService.generateJWT(credentials);
      
      // Validate the generated token
      const validation = await jwtService.validateJWT(token);
      if (!validation.valid) {
        throw new Error('Generated token is invalid');
      }
      
      // Store auth data in sessionStorage (not localStorage) so tokens are
      // scoped to this tab and do not persist across browser restarts.
      // This reduces the token theft surface from XSS — a stolen token cannot
      // be exfiltrated from another tab or retrieved after the session ends.
      sessionStorage.setItem('mme_token', token);
      sessionStorage.setItem('mme_user_id', credentials.userId || 'demo-user');
      sessionStorage.setItem('mme_user_name', credentials.name || 'Demo User');
      sessionStorage.setItem('mme_user_email', credentials.email || 'demo@example.com');
      sessionStorage.setItem('mme_user_role', credentials.role || 'user');
      sessionStorage.setItem('mme_org_id', credentials.orgId || 'demo-org');
      
      console.log('Auth data stored:', {
        token: token.substring(0, 20) + '...',
        userId: credentials.userId || 'demo-user',
        role: credentials.role || 'user',
        orgId: credentials.orgId || 'demo-org'
      });
      
      const userData = {
        id: credentials.userId || 'demo-user',
        userId: credentials.userId || 'demo-user',
        name: credentials.name || 'Demo User',
        email: credentials.email || 'demo@example.com',
        role: credentials.role || 'user',
        orgId: credentials.orgId || 'demo-org',
        token: token,
        expiresAt: validation.expiresAt
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Schedule token refresh
      scheduleTokenRefresh(token);
      
      return { token, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApiKey = async (apiKey) => {
    try {
      setIsLoading(true);

      const data = await apiService.exchangeApiKey(apiKey);
      const token = data.token;

      if (!token) {
        throw new Error('No token returned from exchange');
      }

      const validation = await jwtService.validateJWT(token);
      if (!validation.valid) {
        throw new Error('Returned token is invalid');
      }

      // Wipe React Query cache before storing the new identity (see
      // loginWithGoogle for the rationale).
      queryClient.clear();

      sessionStorage.setItem('mme_token', token);
      // Persist the API key so the /api-key page can show the actual key
      // the user authenticates with (not the ephemeral JWT).
      sessionStorage.setItem('mme_api_key', apiKey);
      sessionStorage.setItem('mme_user_id', data.user_id || validation.payload.userId);
      sessionStorage.setItem('mme_user_role', (validation.payload.role || 'user'));
      sessionStorage.setItem('mme_org_id', data.org_id || validation.payload.org_id);

      const userData = {
        id: data.user_id || validation.payload.userId,
        userId: data.user_id || validation.payload.userId,
        name: validation.payload.name || 'User',
        email: validation.payload.email || '',
        role: (validation.payload.role || 'user'),
        orgId: data.org_id || validation.payload.org_id,
        token,
        expiresAt: validation.expiresAt,
      };

      setUser(userData);
      setIsAuthenticated(true);
      scheduleTokenRefresh(token);

      return { token, user: userData };
    } catch (error) {
      console.error('API key login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Sign in with a Google Identity Services ID token.
  // The credential comes from google.accounts.id.initialize callback in
  // LoginPage. The backend /auth/google verifies the token, finds or creates
  // the user's api_keys record, and returns the MME JWT + api key + profile.
  const loginWithGoogle = async (googleCredential) => {
    try {
      setIsLoading(true);

      const data = await apiService.loginWithGoogle(googleCredential);
      const token = data.token;
      if (!token) {
        throw new Error('No token returned from Google sign-in');
      }

      const validation = await jwtService.validateJWT(token);
      if (!validation.valid) {
        throw new Error('Returned token is invalid');
      }

      // Wipe React Query cache before storing the new identity, so any
      // memories/usage/admin data cached for a previous user is gone before
      // the dashboard mounts and reads its query keys.
      queryClient.clear();

      sessionStorage.setItem('mme_token', token);
      // Store the API key so the /api-key page shows it without an extra fetch.
      if (data.apiKey) sessionStorage.setItem('mme_api_key', data.apiKey);
      sessionStorage.setItem('mme_user_id', data.user_id || validation.payload.userId);
      sessionStorage.setItem('mme_org_id', data.org_id || validation.payload.org_id);
      sessionStorage.setItem('mme_user_email', data.email || validation.payload.email || '');
      sessionStorage.setItem('mme_user_name', data.name || '');
      sessionStorage.setItem('mme_user_role', 'user');
      if (data.picture) sessionStorage.setItem('mme_user_picture', data.picture);

      const userData = {
        id: data.user_id,
        userId: data.user_id,
        name: data.name || 'User',
        email: data.email || '',
        picture: data.picture || '',
        role: 'user',
        orgId: data.org_id,
        token,
        expiresAt: validation.expiresAt,
        isNewUser: !!data.is_new_user,
      };

      setUser(userData);
      setIsAuthenticated(true);
      scheduleTokenRefresh(token);

      return { token, user: userData };
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear auth data
      sessionStorage.removeItem('mme_token');
      sessionStorage.removeItem('mme_api_key');
      sessionStorage.removeItem('mme_user_id');
      sessionStorage.removeItem('mme_user_name');
      sessionStorage.removeItem('mme_user_email');
      sessionStorage.removeItem('mme_user_role');
      sessionStorage.removeItem('mme_user_picture');
      sessionStorage.removeItem('mme_org_id');

      // Wipe React Query cache so the next user (or anonymous reload of the
      // login page) can never see this user's memories/usage/admin data.
      queryClient.clear();

      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasPermission = (permission) => {
    // Simple permission check based on role
    const permissions = {
      user: ['read', 'write'],
      admin: ['read', 'write', 'delete', 'manage'],
      developer: ['read', 'write', 'delete', 'manage', 'debug'],
      system: ['read', 'write', 'delete', 'manage', 'system'],
    };
    
    return permissions[user?.role]?.includes(permission) || false;
  };


  const validateCurrentToken = async () => {
    const token = sessionStorage.getItem('mme_token');
    if (!token) return false;
    
    const validation = await jwtService.validateJWT(token);
    if (!validation.valid) {
      await logout();
      return false;
    }
    
    return true;
  };

  const getToken = () => {
    return sessionStorage.getItem('mme_token');
  };

  const isTokenExpiring = (minutesThreshold = 15) => {
    const token = sessionStorage.getItem('mme_token');
    if (!token) return true;
    
    const expirationTime = jwtService.getTokenExpirationTime(token);
    if (!expirationTime) return true;
    
    const timeUntilExpiry = expirationTime.getTime() - Date.now();
    return timeUntilExpiry < (minutesThreshold * 60 * 1000);
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithApiKey,
    loginWithGoogle,
    logout,
    updateUser,
    hasRole,
    hasPermission,
    refreshToken,
    validateCurrentToken,
    getToken,
    isTokenExpiring,
    jwtService, // Expose JWT service for advanced usage
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default useAuth;
